import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../services/api";
import ProductCard from "../components/ProductCard";
import Navbar from "../components/Navbar";
import { debounce } from "lodash";
import SEO from "../components/SEO";

const CATEGORIES = [
  { key: "SPORT_FASHION", label: "Thời trang thể thao" },
  { key: "GYM_YOGA", label: "Gym & Yoga" },
  { key: "RUNNING", label: "Chạy bộ" },
  { key: "FOOTBALL", label: "Bóng đá" },
  { key: "SWIMMING", label: "Bơi lội" },
  { key: "BADMINTON", label: "Cầu lông" },
  { key: "TENNIS", label: "Tennis" },
  { key: "VOLLEYBALL", label: "Bóng chuyền" },
  { key: "BASKETBALL", label: "Bóng rổ" },
  { key: "ACCESSORIES", label: "Phụ kiện" },
  { key: "TRAINING_EQUIPMENT", label: "Dụng cụ" },
  { key: "SecondHand", label: "Sản phẩm SecondHand" },
];

export default function ProductsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [category, setCategory] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const [priceRange, setPriceRange] = useState({ min: 0, max: 0 });
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sort, setSort] = useState("newest");
  const [loading, setLoading] = useState(false);
  const [brandStats, setBrandStats] = useState([]);
  const [categoryStats, setCategoryStats] = useState([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Parse URL parameters
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const brandParam = params.get("brand") || "";
    const categoryParam = params.get("category") || "";
    const subParam = params.get("sub") || "";
    const searchParam = params.get("search") || "";
    const pageParam = parseInt(params.get("page")) || 1;

    setSelectedBrand(brandParam);
    setCategory(categoryParam);
    setSelectedSubcategory(subParam);
    setSearch(searchParam);
    setSearchInput(searchParam);
    setPage(pageParam);
  }, [location.search]);

  useEffect(() => {
    let ignore = false;
    // Xóa stats cũ để tránh nháy dữ liệu sai khi chuyển mode
    setBrandStats([]);
    setCategoryStats([]);

    const loadStats = async () => {
      try {
        const isSH = category === "SecondHand";
        const q = isSH ? "?isSecondHand=true" : "";
        const [brandRes, catRes] = await Promise.all([
          api.get(`/products/brands${q}`),
          api.get(`/products/categories${q}`),
        ]);
        if (!ignore) {
          setBrandStats(brandRes.data || []);
          setCategoryStats(catRes.data || []);
        }
      } catch (err) {
        if (!ignore) console.error("Error fetching stats:", err);
      }
    };

    loadStats();
    return () => {
      ignore = true;
    };
  }, [category]);

  useEffect(() => {
    const handler = debounce((val) => {
      const params = new URLSearchParams(location.search);
      if (val) {
        params.set("search", val);
      } else {
        params.delete("search");
      }
      params.delete("page");
      navigate(`/products?${params.toString()}`);
    }, 500);

    handler(searchInput);
    return () => handler.cancel();
  }, [searchInput]);

  useEffect(() => {
    let ignore = false;

    const loadData = async () => {
      setLoading(true);
      try {
        const params = {
          page,
          limit,
          sort,
        };
        if (category === "SecondHand") {
          params.isSecondHand = true;
        } else if (category) {
          params.category = category;
        }

        if (selectedBrand) params.brand = selectedBrand;
        if (selectedSubcategory) params.subcategory = selectedSubcategory;
        if (search) params.search = search;
        if (priceRange.min > 0) params.minPrice = priceRange.min;
        if (priceRange.max > 0) params.maxPrice = priceRange.max;

        const res = await api.get("/products", { params });
        if (!ignore) {
          setProducts(res.data?.products || []);
          setPages(res.data?.pages || 1);
          setTotal(res.data?.total || 0);
        }
      } catch (err) {
        if (!ignore) console.error(err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };

    loadData();

    return () => {
      ignore = true;
    };
  }, [
    page,
    limit,
    sort,
    category,
    selectedBrand,
    selectedSubcategory,
    search,
    priceRange,
  ]);

  const handlePriceChange = (min, max) => {
    if (priceRange.min === min && priceRange.max === max) {
      setPriceRange({ min: 0, max: 0 });
    } else {
      setPriceRange({ min, max });
    }
  };

  const handlePageChange = (p) => {
    if (p === page || p === "...") return;
    const params = new URLSearchParams(location.search);
    params.set("page", p);
    navigate(`/products?${params.toString()}`);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPageNumbers = () => {
    const delta = window.innerWidth < 640 ? 1 : 2;
    const range = [];
    const rangeWithDots = [];
    let l;

    range.push(1);
    for (let i = page - delta; i <= page + delta; i++) {
      if (i < pages && i > 1) {
        range.push(i);
      }
    }
    if (pages > 1) {
      range.push(pages);
    }

    for (let i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l !== 1) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  return (
    <div
      className="bg-white dark:bg-slate-950 min-h-screen flex flex-col transition-colors duration-300"
      style={{
        zoom: "0.8",
        MozTransform: "scale(0.8)",
        MozTransformOrigin: "top center",
      }}
    >
      <SEO
        title="Tất cả sản phẩm"
        description="Khám phá bộ sưu tập giày thể thao chính hãng tại TaHa Shoes. Nike, Adidas, Jordan, MLB... đa dạng mẫu mã, giá tốt nhất thị trường."
        keywords="giày thể thao, nike, adidas, sneaker, mua giày online"
      />
      <Navbar />
      <div className="pt-16 sm:pt-28 lg:pt-36 pb-20 max-w-[1440px] mx-auto w-full px-4 flex-grow">
        <div className="flex flex-col lg:flex-row gap-6 w-full">
          {/* Mobile Filter Toggle */}
          <div className="lg:hidden flex justify-between items-center bg-white dark:bg-slate-900 px-4 py-3 rounded-xl shadow-sm border mt-4 border-gray-100 dark:border-slate-800">
            <button
              onClick={() => setShowMobileFilters(true)}
              className="flex items-center gap-2 text-gray-700 dark:text-slate-300 font-bold"
            >
              <svg
                className="w-5 h-5 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                />
              </svg>
              <span className="text-sm">Bộ lọc & Tìm kiếm</span>
            </button>
          </div>

          {/* Sidebar / Mobile Drawer */}
          <aside
            className={`fixed inset-0 z-[80] lg:static lg:block lg:w-60 flex-shrink-0 transition-transform duration-300 ${
              showMobileFilters
                ? "translate-x-0"
                : "-translate-x-full lg:translate-x-0"
            }`}
          >
            {/* Overlay for mobile */}
            <div
              className={`absolute inset-0 bg-black/50 backdrop-blur-sm lg:hidden transition-opacity ${
                showMobileFilters
                  ? "opacity-100"
                  : "opacity-0 pointer-events-none"
              }`}
              onClick={() => setShowMobileFilters(false)}
            ></div>

            <div
              className={`relative bg-white dark:bg-slate-950 w-80 lg:w-auto h-full lg:h-auto overflow-y-auto lg:overflow-visible p-6 lg:p-0 space-y-8 lg:sticky lg:top-20 transition-transform duration-300 ${
                showMobileFilters
                  ? "translate-x-0"
                  : "-translate-x-full lg:translate-x-0"
              }`}
            >
              {/* Mobile Header */}
              <div className="flex items-center justify-between lg:hidden mb-6">
                <h2 className="text-xl font-black text-gray-900 dark:text-white">
                  BỘ LỌC
                </h2>
                <button
                  onClick={() => setShowMobileFilters(false)}
                  className="p-2 bg-gray-100 dark:bg-slate-800 rounded-full"
                >
                  <svg
                    className="w-6 h-6 text-gray-600 dark:text-slate-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Danh muc sản phẩm */}
              <div>
                <h3 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white mb-4 sm:mb-6 flex items-center gap-2 sm:gap-3">
                  <span className="w-1 h-5 sm:w-1.5 sm:h-6 bg-blue-600 rounded-full"></span>
                  {category === "SecondHand"
                    ? "SECONDHAND"
                    : "Danh mục sản phẩm"}
                </h3>

                <button
                  onClick={() => {
                    if (category === "SecondHand") {
                      navigate("/products?category=SecondHand");
                    } else {
                      navigate("/products");
                    }
                    setShowMobileFilters(false);
                  }}
                  className={`w-full flex items-center justify-between p-4 rounded-2xl transition-all duration-500 group relative overflow-hidden ${
                    !category && !selectedBrand
                      ? "bg-blue-600 text-white shadow-[0_10px_25px_rgba(37,99,235,0.25)]"
                      : "bg-gray-50 dark:bg-slate-900/50 text-gray-700 dark:text-slate-300 hover:text-blue-600 border border-transparent dark:border-slate-800 hover:border-blue-200 dark:hover:border-blue-900/30"
                  }`}
                >
                  <div className="flex items-center gap-2 sm:gap-3 relative z-10">
                    <div
                      className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg sm:rounded-xl flex items-center justify-center transition-colors ${
                        (!category || category === "SecondHand") &&
                        !selectedBrand
                          ? "bg-white/20"
                          : "bg-white dark:bg-slate-800 shadow-sm"
                      }`}
                    >
                      <svg
                        className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M4 6h16M4 12h16M4 18h7"
                        />
                      </svg>
                    </div>
                    <span className="font-bold tracking-tight text-sm sm:text-base">
                      {category === "SecondHand"
                        ? "Tất cả đồ SecondHand"
                        : "Tất cả sản phẩm"}
                    </span>
                  </div>
                  <svg
                    className={`w-5 h-5 relative z-10 transition-transform duration-500 ${
                      !category && !selectedBrand
                        ? "translate-x-0"
                        : "group-hover:translate-x-1 opacity-0 group-hover:opacity-100"
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                  {!category && !selectedBrand && (
                    <div className="absolute top-0 right-0 w-24 h-24 bg-white/10 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
                  )}
                </button>

                <div className="mt-4 sm:mt-6">
                  <h4 className="text-[10px] sm:text-xs font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-2 sm:mb-3 px-4">
                    Thương hiệu
                  </h4>
                  <div className="space-y-1">
                    {(() => {
                      const stats =
                        brandStats.length > 0 ? brandStats : categoryStats;
                      const unique = [];
                      stats.forEach((item) => {
                        let displayName = item.name.trim();
                        if (displayName.toLowerCase().includes("jordan")) {
                          displayName = "Jordan";
                        }

                        const existing = unique.find(
                          (u) =>
                            u.name.toLowerCase() === displayName.toLowerCase(),
                        );
                        if (existing) {
                          existing.count += item.count;
                        } else {
                          unique.push({ ...item, name: displayName });
                        }
                      });

                      return unique.map((item) => {
                        const isSelected =
                          selectedBrand
                            .toLowerCase()
                            .includes(item.name.toLowerCase()) ||
                          item.name
                            .toLowerCase()
                            .includes(selectedBrand.toLowerCase() || "___") ||
                          category.toLowerCase() === item.name.toLowerCase();

                        return (
                          <button
                            key={item.name}
                            onClick={() => {
                              const params = new URLSearchParams(
                                location.search,
                              );
                              params.delete("sub");
                              params.delete("page");

                              if (category === "SecondHand") {
                                params.set("category", "SecondHand");
                                params.set("brand", item.name);
                              } else {
                                if (brandStats.length > 0) {
                                  params.set("brand", item.name);
                                  params.delete("category");
                                } else {
                                  params.set("category", item.name);
                                  params.delete("brand");
                                }
                              }
                              navigate(`/products?${params.toString()}`);
                              setShowMobileFilters(false);
                            }}
                            className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-300 group ${
                              isSelected
                                ? "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-bold"
                                : "text-gray-600 dark:text-slate-400 hover:bg-gray-50/80 dark:hover:bg-slate-900/50 hover:text-gray-900 dark:hover:text-white"
                            }`}
                          >
                            <span
                              className={`text-[13px] sm:text-base transition-transform duration-300 ${isSelected ? "translate-x-1" : "group-hover:translate-x-1"}`}
                            >
                              {item.name}
                            </span>
                          </button>
                        );
                      });
                    })()}
                  </div>
                </div>
              </div>

              {/* Khoảng giá */}
              <div>
                <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
                  Khoảng giá
                </h3>
                <div className="space-y-3">
                  {[
                    { label: "Dưới 500.000₫", min: 0, max: 500000 },
                    {
                      label: "500.000₫ - 1.000.000₫",
                      min: 500000,
                      max: 1000000,
                    },
                    { label: "Trên 1.000.000₫", min: 1000000, max: 0 },
                  ].map((range) => (
                    <label
                      key={range.label}
                      className="flex items-center gap-3 cursor-pointer group"
                    >
                      <div className="relative flex items-center">
                        <input
                          type="checkbox"
                          className="peer appearance-none w-5 h-5 border-2 border-gray-300 dark:border-slate-700 rounded checked:bg-blue-600 dark:checked:bg-blue-500 checked:border-blue-600 dark:checked:border-blue-500 transition-all cursor-pointer"
                          checked={
                            priceRange.min === range.min &&
                            priceRange.max === range.max
                          }
                          onChange={() => {
                            handlePriceChange(range.min, range.max);
                            // Keep mobile filters open when selecting price
                          }}
                        />
                        <svg
                          className="absolute w-3.5 h-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity left-0.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={3}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                      <span className="text-sm sm:text-base text-gray-600 dark:text-slate-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                        {range.label}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Mobile apply button */}
              <button
                onClick={() => setShowMobileFilters(false)}
                className="w-full lg:hidden bg-blue-600 text-white py-4 rounded-2xl font-black shadow-lg shadow-blue-200 dark:shadow-none"
              >
                XEM KẾT QUẢ
              </button>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0 w-full">
            <div className="bg-white dark:bg-slate-900 p-2 sm:p-4 rounded-2xl shadow-sm border border-gray-100 dark:border-slate-800 mb-4 sm:mb-8">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex-1 relative">
                  <input
                    type="text"
                    placeholder="Tìm sản phẩm..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full pl-9 pr-4 py-1.5 sm:py-2.5 bg-gray-50 dark:bg-slate-800 border-none rounded-xl focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 text-xs sm:text-base text-gray-900 dark:text-white transition-all"
                  />
                  <svg
                    className="absolute left-3.5 top-1 sm:top-3 w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>

                <div className="flex items-center gap-4">
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="bg-gray-50 dark:bg-slate-800 border-none rounded-xl px-3 sm:px-4 py-1.5 sm:py-2.5 text-[11px] sm:text-sm text-gray-700 dark:text-slate-300 focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 cursor-pointer"
                  >
                    <option value="newest">Mới nhất</option>
                    <option value="price_asc">Giá: thấp → cao</option>
                    <option value="price_desc">Giá: cao → thấp</option>
                  </select>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {Array.from({ length: 9 }).map((_, index) => (
                  <div
                    key={index}
                    className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-gray-100 dark:border-slate-800 flex flex-col h-full animate-pulse"
                  >
                    <div className="w-full aspect-[4/5] bg-gray-200 dark:bg-slate-800 rounded-xl mb-4"></div>
                    <div className="flex-1 flex flex-col">
                      <div className="flex justify-between items-start mb-3">
                        <div className="h-3 bg-gray-200 dark:bg-slate-800 rounded w-1/3"></div>
                        <div className="w-8 h-4 bg-gray-200 dark:bg-slate-800 rounded"></div>
                      </div>
                      <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded w-full mb-2"></div>
                      <div className="h-4 bg-gray-200 dark:bg-slate-800 rounded w-2/3 mb-4"></div>
                      <div className="mt-auto pt-4 border-t border-gray-50 dark:border-slate-800 flex items-center justify-between">
                        <div className="h-6 bg-gray-200 dark:bg-slate-800 rounded w-5/12"></div>
                        <div className="hidden sm:block w-10 h-10 rounded-xl bg-gray-200 dark:bg-slate-800"></div>
                      </div>
                      <div className="sm:hidden mt-4 h-11 bg-gray-200 dark:bg-slate-800 rounded-xl w-full"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-32 bg-gray-50 dark:bg-slate-900/50 rounded-3xl border-2 border-dashed border-gray-200 dark:border-slate-800 transition-colors">
                <p className="text-gray-500 dark:text-slate-400 text-lg">
                  Không tìm thấy sản phẩm nào
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                  {products.map((p) => (
                    <ProductCard key={p._id} product={p} />
                  ))}
                </div>

                {/* Pagination */}
                {pages > 1 && (
                  <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-12 w-full px-2">
                    <button
                      disabled={page <= 1}
                      onClick={() => handlePageChange(page - 1)}
                      className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 text-gray-600 dark:text-slate-400 hover:bg-blue-600 hover:text-white hover:border-blue-600 disabled:opacity-50 disabled:hover:bg-white dark:disabled:hover:bg-slate-900 transition-all duration-300"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 19l-7-7 7-7"
                        />
                      </svg>
                    </button>
                    <div className="flex items-center gap-1 sm:gap-2">
                      {getPageNumbers().map((p, idx) => (
                        <button
                          key={idx}
                          disabled={p === "..."}
                          onClick={() => handlePageChange(p)}
                          className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl font-bold transition-all duration-300 ${
                            p === page
                              ? "bg-blue-600 text-white shadow-lg shadow-blue-200 dark:shadow-blue-900/20"
                              : p === "..."
                                ? "text-gray-400 cursor-default"
                                : "bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 text-gray-600 dark:text-slate-400 hover:bg-blue-50 dark:hover:bg-slate-800"
                          }`}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                    <button
                      disabled={page >= pages}
                      onClick={() => handlePageChange(page + 1)}
                      className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center rounded-xl bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 text-gray-600 dark:text-slate-400 hover:bg-blue-600 hover:text-white hover:border-blue-600 disabled:opacity-50 disabled:hover:bg-white dark:disabled:hover:bg-slate-900 transition-all duration-300"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
